import React from "react";
import { Text, Box, Card } from "@salestrak/ui";
import { useRouter } from "#app/lib/hooks/router-compat";
import { SalesTrakLogo } from "#app/components/brand/salestrak-logo";
import { LegalLinks } from "./legal-links";
import type { Omittable } from "@salestrak/ui";

type AuthLayoutProps = Omittable<{
  heading: string;
  subheading?: string;
  children: React.ReactNode;
  footerText?: string;
  footerLinkText?: string;
  footerLinkHref?: string;
  /** The screen's verb phrase for the legal line: "By {action}, you agree…". */
  legalAction?: string;
}>;

export function AuthLayout({
  heading,
  subheading,
  children,
  footerText,
  footerLinkText,
  footerLinkHref,
  legalAction,
}: AuthLayoutProps) {
  const router = useRouter();

  return (
    <Box direction="column" minHeight="100dvh" background="background">
      <Box
        direction="column"
        align="center"
        justify="center"
        paddingX="lg"
        flex={1}
      >
        {/* Block separation is bottom padding, matching the workspace screens:
            24 logo→heading, 12 heading→subtext, 32 subtext→form. The column
            is the form's 400px plus the card's 24px inset on each side. */}
        <Box fill="width" maxWidth={448}>
          {/* The page, the fields and the muted disc all sit within 3 L of
              each other in light mode, so a `muted` disc reads as a smudge.
              The mark sits on a solid `primary` tile instead: the one hard
              edge on the screen, and the brand anchor the card hangs from. */}
          <Box direction="column" align="center" paddingBottom="lg">
            <Box
              width={56}
              height={56}
              rounded="xl"
              background="primary"
              direction="column"
              align="center"
              justify="center"
            >
              <SalesTrakLogo showText={false} height={28} onPrimary />
            </Box>
          </Box>

          <Box paddingBottom={subheading ? "base" : "xl"}>
            <Text as="h1" size="xl" weight="medium" align="center">
              {heading}
            </Text>
          </Box>

          {subheading ? (
            <Box paddingBottom="xl">
              {/* as="p": an inline span is fit-content, so text-center is a no-op. */}
              <Text as="p" size="base" weight="medium" variant="muted" align="center">
                {subheading}
              </Text>
            </Box>
          ) : null}

          {/* The form sits on a `card` surface. The library Input keeps the
              page fill, which only reads as a field when the surface UNDER it
              is a step lighter — straight on the page, a field and the canvas
              share one fill and the hairline is all that separates them. */}
          <Card surface="card" radius="xl" padding="lg">
            {children}
          </Card>

          {footerText && footerLinkText && footerLinkHref ? (
            <Box
              direction="row"
              align="center"
              justify="center"
              gap="xs"
              paddingTop="lg"
            >
              <Text variant="muted" size="sm">
                {footerText}
              </Text>
              <Box
                as="button"
                type="button"
                cursor="pointer"
                onClick={() => router.replace(footerLinkHref)}
              >
                <Text size="sm" weight="medium" underline="hover">
                  {footerLinkText}
                </Text>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* No opacity dim: the sentence is already muted ink, and dimming the
          two links with it drops them under contrast. */}
      <Box
        direction="column"
        align="center"
        paddingX="lg"
        paddingBottom="lg"
        paddingTop="xs"
      >
        <LegalLinks action={legalAction} />
      </Box>
    </Box>
  );
}
